/**
 * (Stripe's subscription statuses documentation)[https://stripe.com/docs/billing/subscriptions/overview#subscription-statuses]
 */

import {
    SUBSCRIPTION_STATUS_ACTIVE,
    SUBSCRIPTION_STATUS_CANCELLED,
    SUBSCRIPTION_STATUS_FAILED,
    SUBSCRIPTION_STATUS_INCOMPLETE,
    SUBSCRIPTION_STATUS_INCOMPLETE_EXPIRED,
    SUBSCRIPTION_STATUS_PAST_DUE,
    SUBSCRIPTION_STATUS_PENDING,
    SUBSCRIPTION_STATUS_UNPAID,
} from '../constants'
import { addMonths, getDate, getIsDateExceeded } from '../helpers/dates'

export const isActiveSubscription = (status) => status === SUBSCRIPTION_STATUS_ACTIVE // a subscription becomes Active if its most recent payment attempt is successful

export const isPendingSubscription = (status) => status === SUBSCRIPTION_STATUS_PENDING // a Pending subscription is created by the back-end as soon as we request to initiate the first subscription payment, but it doesn't mean the customer has ever actually paid for it

export const isCancelledSubscription = (status) => status === SUBSCRIPTION_STATUS_CANCELLED // a subscription becomes Cancelled after having been Active for a while. Originally it was support who did it. Now it's usually triggered by customers.

export const isIncompleteSubscription = (status) => status === SUBSCRIPTION_STATUS_INCOMPLETE // a subscription becomes Incomplete if its initial payment attempt fails straight away

export const isExpiredSubscription = (status) =>
    [
        SUBSCRIPTION_STATUS_INCOMPLETE_EXPIRED, // a subscription becomes IncompleteExpired if its first invoice isn't paid within 23h
        SUBSCRIPTION_STATUS_FAILED, // this is not an actual Stripe status. Subscriptions are supposed to become IncompleteExpired, but they become Failed first until back-end's WebJob to synchronize Stripe's subscriptions kicks off within a 24h window. And it's (at least currently) useless.
    ].includes(status)

export const isRecoverableSubscription = (status) =>
    [
        SUBSCRIPTION_STATUS_PAST_DUE, // a subscription becomes PastDue after first renew payment fails
        SUBSCRIPTION_STATUS_UNPAID, // a subscription becomes Unpaid after the 4 (on days 0, 5, 12 and 17) renew payments fail
    ].includes(status)

export const isActiveOrRecoverableSubscription = (status) =>
    isActiveSubscription(status) || isRecoverableSubscription(status)

export const isTerminatedSubscription = (status) => isExpiredSubscription(status) || isCancelledSubscription(status)

export const hasFirstPaymentAttemptFailed = (status) =>
    isIncompleteSubscription(status) || isExpiredSubscription(status)

export const hasFirstPaymentAttemptSucceeded = (status) =>
    isActiveOrRecoverableSubscription(status) || isCancelledSubscription(status)

export const hasNotPaidForSubscription = (status) =>
    isPendingSubscription(status) || hasFirstPaymentAttemptFailed(status)

export const doesSubscriptionHaveMissingPayments = isRecoverableSubscription
export const doesSubscriptionRequireAction = isRecoverableSubscription

export const isCancellableSubscription = isActiveOrRecoverableSubscription

export const shouldResubscribe = isTerminatedSubscription

export const shouldSubscribe = (status) => !status || isPendingSubscription(status) || shouldResubscribe(status)

export const hasSubscription = (subscriptions) => !!subscriptions?.length > 0

export const getHasSubscriptionReactivationWithPenaltyPeriodPassed = (cancellationDateUTC) => {
    const reactivationAllowedDate = addMonths(4)(getDate(cancellationDateUTC))

    return getIsDateExceeded(getDate(), reactivationAllowedDate)
}
